import type {
  AgentTicketCreateOptions,
  DirectoryUser,
  PagedPayload,
  TicketDetail,
} from "@noverik/contracts";
import { Building2, FileText, Paperclip, Search, Send, UserRound, X } from "lucide-react";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { apiRequest, ApiRequestError } from "../lib/api";
import { encodeAttachments } from "../lib/files";
import { RichTextEditor, hasRichTextContent } from "../components/RichTextEditor";

export function AgentNewTicketPage() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [options, setOptions] = useState<AgentTicketCreateOptions | null>(null);
  const [users, setUsers] = useState<DirectoryUser[]>([]);
  const [userQuery, setUserQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState<DirectoryUser | null>(null);
  const [topicId, setTopicId] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [source, setSource] = useState("Phone");
  const [assignee, setAssignee] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [notifyUser, setNotifyUser] = useState(true);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    apiRequest<AgentTicketCreateOptions>("/agent/ticket-options")
      .then((payload) => {
        if (!active) return;
        setOptions(payload);
        if (payload.defaults.topicId) setTopicId(String(payload.defaults.topicId));
        if (payload.defaults.departmentId) setDepartmentId(String(payload.defaults.departmentId));
        if (payload.defaults.source) setSource(payload.defaults.source);
      })
      .catch((err) => {
        if (!active) return;
        setError(err instanceof ApiRequestError ? err.message : "Não foi possível carregar as opções do chamado.");
      });

    return () => {
      active = false;
    };
  }, []);

  const searchUsers = useCallback(async (query: string) => {
    setSearchingUsers(true);
    setError(null);

    try {
      const params = new URLSearchParams({ page: "1", limit: "20" });
      if (query.trim()) params.set("q", query.trim());
      const payload = await apiRequest<PagedPayload<DirectoryUser>>(
        "/users?" + params.toString(),
      );
      setUsers(payload.items);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "Não foi possível buscar clientes.");
    } finally {
      setSearchingUsers(false);
    }
  }, []);

  useEffect(() => {
    void searchUsers("");
  }, [searchUsers]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();

    if (!session?.csrfToken) {
      setError("Sua sessão expirou. Atualize a página.");
      return;
    }

    if (!selectedUser) {
      setError("Selecione o cliente do chamado.");
      return;
    }

    const parsedTopic = Number(topicId);
    if (!Number.isInteger(parsedTopic) || parsedTopic <= 0) {
      setError("Selecione um tópico de ajuda.");
      return;
    }

    if (!hasRichTextContent(message)) {
      setError("Descreva a solicitação antes de criar o chamado.");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const attachments = await encodeAttachments(files);
      const ticket = await apiRequest<TicketDetail>("/agent/tickets", {
        method: "POST",
        csrfToken: session.csrfToken,
        body: {
          userId: selectedUser.id,
          topicId: parsedTopic,
          subject,
          message,
          departmentId: departmentId ? Number(departmentId) : null,
          source,
          assignee: assignee || null,
          attachments,
          notifyUser,
        },
      });

      navigate("/desk/chamados/" + ticket.number, { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiRequestError || err instanceof Error
          ? err.message
          : "Não foi possível abrir o chamado.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">ATENDIMENTO</span>
          <h1>Novo chamado</h1>
          <p>Abra uma solicitação em nome de um cliente usando os dados e regras do osTicket.</p>
        </div>
      </div>

      <form className="agent-new-ticket" onSubmit={submit}>
        <section className="ticket-form-card">
          <div className="ticket-form-section">
            <div className="form-section-heading">
              <span className="meta-icon green"><UserRound size={20} /></span>
              <div>
                <strong>Cliente</strong>
                <p>Selecione quem está solicitando o atendimento.</p>
              </div>
            </div>

            {selectedUser ? (
              <div className="selected-customer">
                <div className="avatar">{selectedUser.name.slice(0, 2).toUpperCase()}</div>
                <div>
                  <strong>{selectedUser.name}</strong>
                  <span>{selectedUser.email}</span>
                  {selectedUser.organization && (
                    <small><Building2 size={13} /> {selectedUser.organization.name}</small>
                  )}
                </div>
                <button type="button" className="button secondary" onClick={() => setSelectedUser(null)}>
                  Trocar cliente
                </button>
              </div>
            ) : (
              <>
                <div className="searchbox inline customer-picker-search">
                  <Search size={17} />
                  <input
                    value={userQuery}
                    onChange={(event) => setUserQuery(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        void searchUsers(userQuery);
                      }
                    }}
                    placeholder="Buscar cliente por nome, e-mail ou organização..."
                  />
                  <button type="button" className="search-submit" onClick={() => void searchUsers(userQuery)}>Buscar</button>
                </div>

                <div className="customer-picker">
                  {searchingUsers && <div className="table-loading">Buscando clientes...</div>}
                  {!searchingUsers && users.map((user) => (
                    <button key={user.id} type="button" onClick={() => setSelectedUser(user)}>
                      <div className="avatar small">{user.name.slice(0, 2).toUpperCase()}</div>
                      <div>
                        <strong>{user.name}</strong>
                        <span>{user.email}</span>
                        <small>{user.organization?.name || "Sem organização"}</small>
                      </div>
                    </button>
                  ))}
                  {!searchingUsers && users.length === 0 && (
                    <div className="table-empty">
                      <strong>Nenhum cliente encontrado.</strong>
                      <span>Cadastre o cliente na área Clientes e tente novamente.</span>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="ticket-form-section">
            <div className="form-section-heading">
              <span className="meta-icon blue"><FileText size={20} /></span>
              <div>
                <strong>Dados do chamado</strong>
                <p>Defina classificação, origem e conteúdo da solicitação.</p>
              </div>
            </div>

            <div className="form-grid">
              <label className="field">
                <span>Tópico de ajuda</span>
                <select value={topicId} onChange={(event) => setTopicId(event.target.value)} required>
                  <option value="">Selecione</option>
                  {options?.topics.map((topic) => (
                    <option key={topic.id} value={topic.id}>{topic.name}</option>
                  ))}
                </select>
              </label>

              <label className="field">
                <span>Departamento</span>
                <select value={departmentId} onChange={(event) => setDepartmentId(event.target.value)}>
                  <option value="">Automático pelo tópico</option>
                  {options?.departments.map((department) => (
                    <option key={department.id} value={department.id}>{department.name}</option>
                  ))}
                </select>
              </label>

              <label className="field">
                <span>Origem</span>
                <select value={source} onChange={(event) => setSource(event.target.value)}>
                  {options?.sources.map((item) => (
                    <option key={item.id} value={item.id}>{item.name}</option>
                  ))}
                </select>
              </label>

              <label className="field">
                <span>Atribuir para</span>
                <select value={assignee} onChange={(event) => setAssignee(event.target.value)}>
                  <option value="">Sem atribuição inicial</option>
                  {options?.assignees.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.type === "team" ? "Equipe: " : ""}{item.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="field form-span">
                <span>Assunto</span>
                <input
                  value={subject}
                  onChange={(event) => setSubject(event.target.value)}
                  maxLength={255}
                  placeholder="Resumo do atendimento"
                  required
                />
              </label>

              <label className="field form-span">
                <span>Descrição</span>
                <RichTextEditor
                  value={message}
                  onChange={setMessage}
                  disabled={submitting}
                  minHeight={215}
                  label="Descrição da solicitação"
                  placeholder="Descreva a solicitação do cliente..."
                />
              </label>
            </div>
          </div>

          <div className="ticket-form-section">
            <div className="form-section-heading">
              <span className="meta-icon purple"><Paperclip size={20} /></span>
              <div>
                <strong>Anexos e comunicação</strong>
                <p>Inclua arquivos e escolha se o cliente deve receber a notificação inicial.</p>
              </div>
            </div>

            <label className="upload-zone">
              <Paperclip size={20} />
              <strong>Selecionar arquivos</strong>
              <span>Até 10 arquivos por envio.</span>
              <input
                type="file"
                multiple
                onChange={(event) => {
                  const selected = Array.from(event.target.files ?? []);
                  setFiles((current) => [...current, ...selected].slice(0, 10));
                  event.target.value = "";
                }}
              />
            </label>

            {files.length > 0 && (
              <div className="file-list">
                {files.map((file, index) => (
                  <div className="file-chip" key={file.name + file.lastModified + index}>
                    <div>
                      <strong>{file.name}</strong>
                      <span>{Math.max(1, Math.round(file.size / 1024))} KB</span>
                    </div>
                    <button type="button" onClick={() => setFiles((current) => current.filter((_, position) => position !== index))}>
                      <X size={15} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <label className="check-row">
              <input type="checkbox" checked={notifyUser} onChange={(event) => setNotifyUser(event.target.checked)} />
              <span>
                <strong>Notificar o cliente</strong>
                <small>Usar os avisos configurados no osTicket para a abertura do chamado.</small>
              </span>
            </label>
          </div>

          {error && <div className="login-message form-error">{error}</div>}

          <div className="form-actions">
            <button type="button" className="button secondary" disabled={submitting} onClick={() => navigate(-1)}>
              Cancelar
            </button>
            <button className="button primary" disabled={submitting || !selectedUser}>
              <Send size={17} />
              {submitting ? "Criando chamado..." : "Criar chamado"}
            </button>
          </div>
        </section>
      </form>
    </div>
  );
}
